/**
 * Consistent error types for all WayAI workers
 * These provide structured error responses with proper HTTP status codes.
 */

// Leaf module (imports nothing), so this cannot cycle back through errors.ts.
import { redactSecrets } from './redact';

/**
 * Symbol brand for AppError identification across module boundaries.
 * Using Symbol.for() ensures the same symbol is used across all bundles,
 * solving the instanceof problem in monorepos.
 */
const APP_ERROR_BRAND = Symbol.for('wayai.AppError');

export class AppError extends Error {
  readonly [APP_ERROR_BRAND] = true;

  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AppError';
  }

  toJSON() {
    return {
      error: this.code,
      message: this.message,
      ...(this.details && { details: this.details }),
    };
  }
}

/**
 * 400 - Bad Request: Invalid input data
 */
export class ValidationError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, 'VALIDATION_ERROR', 400, details);
    this.name = 'ValidationError';
  }
}

/**
 * 401 - Unauthorized: Authentication required
 */
export class AuthenticationError extends AppError {
  constructor(message = 'Authentication required') {
    super(message, 'AUTHENTICATION_ERROR', 401);
    this.name = 'AuthenticationError';
  }
}

/**
 * 403 - Forbidden: Permission denied
 */
export class AuthorizationError extends AppError {
  constructor(message = 'Permission denied') {
    super(message, 'AUTHORIZATION_ERROR', 403);
    this.name = 'AuthorizationError';
  }
}

/**
 * 404 - Not Found: Resource doesn't exist
 */
export class NotFoundError extends AppError {
  readonly resource: string;
  constructor(resource: string) {
    super(`${resource} not found`, 'NOT_FOUND', 404);
    this.name = 'NotFoundError';
    this.resource = resource;
  }
}

/**
 * 409 - Conflict: Resource already exists or state conflict
 */
export class ConflictError extends AppError {
  constructor(message: string, details?: Record<string, unknown>) {
    super(message, 'CONFLICT', 409, details);
    this.name = 'ConflictError';
  }
}

/**
 * 429 - Too Many Requests: Rate limit exceeded
 */
export class RateLimitError extends AppError {
  constructor(retryAfter?: number) {
    super('Rate limit exceeded', 'RATE_LIMIT_EXCEEDED', 429, { retryAfter });
    this.name = 'RateLimitError';
  }
}

/**
 * The code of the refusal an app-channel send gets when its end user has already sent the hub's
 * `end_user_daily_message_limit` in the current 24-hour window (wayai#4948). The web and mobile
 * apps read it off the refusal to tell the user why the message did not send.
 */
export const END_USER_DAILY_LIMIT_CODE = 'END_USER_DAILY_LIMIT';

/**
 * A customer page's refusal (403) when no one customer can be named for its viewer (wayai#5333 decision 9):
 * an expected state of the viewer's account, never a defect. Re-exported by `contracts/pages.ts`, which
 * states it.
 */
export const PAGE_CUSTOMER_UNAVAILABLE_CODE = 'PAGE_CUSTOMER_UNAVAILABLE';

/** 429 — the sender has reached the hub's daily message limit for its end users. */
export class EndUserDailyLimitError extends AppError {
  constructor() {
    super('Daily message limit reached for this hub', END_USER_DAILY_LIMIT_CODE, 429);
    this.name = 'EndUserDailyLimitError';
  }
}

/**
 * Providers that can surface an upstream HTTP error or rate-limit. Typed (not
 * bare string) so adapter throw sites get typo protection.
 */
export type LlmProvider = 'anthropic' | 'openai' | 'openrouter' | 'gemini' | 'xai' | 'typesafe';

/**
 * Internal control-flow signal: upstream LLM provider returned 429.
 * Caught in ConversationDO.handleProcess to reschedule the turn via the
 * local alarm instead of failing. Not an HTTP response — distinct from
 * the gateway-level RateLimitError above.
 */
const LLM_RATE_LIMIT_BRAND = Symbol.for('wayai.LlmRateLimitError');

export class LlmRateLimitError extends Error {
  readonly [LLM_RATE_LIMIT_BRAND] = true;

  constructor(
    public readonly provider: LlmProvider,
    public readonly retryAfterMs: number,
  ) {
    super(`${provider} rate limited; retry after ${retryAfterMs}ms`);
    this.name = 'LlmRateLimitError';
  }
}

export function isLlmRateLimitError(error: unknown): error is LlmRateLimitError {
  return (
    typeof error === 'object' &&
    error !== null &&
    LLM_RATE_LIMIT_BRAND in error &&
    (error as Record<symbol, unknown>)[LLM_RATE_LIMIT_BRAND] === true
  );
}

/**
 * Internal signal: an upstream LLM provider returned a non-OK HTTP response
 * OTHER than 429 (429 is LlmRateLimitError — rescheduled, not failed). Carries
 * the HTTP status so the catch site can classify the failure.
 *
 * An upstream 4xx (401 invalid/revoked key, 403, 400) is almost always a
 * customer-credential/config problem — degraded-but-handled and surfaced to the
 * user — so it belongs at logger.warn (Loki only), NOT logger.error (Sentry →
 * autofix). A transient, retryable 503/529 (provider overloaded/unavailable)
 * reschedules the turn instead — see isRetryableLlmError (gh #2659). Other 5xx /
 * transport / our-own faults stay at error. Mirrors the MCP McpError +
 * isExternalMcpHttpError precedent (gh #2534, #2539).
 *
 * `message` keeps the adapter's existing human string ("OpenAI API error: 401 -
 * …") so callers/tests are unchanged; `status` + `provider` are the structured
 * fields the catch site logs. The catch site MUST log only those two — never the
 * message/body, which can embed a credential fragment (e.g. the OpenAI 401 body
 * "Incorrect API key provided: sk-proj-…").
 */
const LLM_PROVIDER_HTTP_BRAND = Symbol.for('wayai.LlmProviderHttpError');

/**
 * Credential-safe structured fields lifted from an upstream LLM provider's error
 * body. These are enum / field-path scalars — OpenAI & OpenRouter
 * `error.type|code|param`, Anthropic `error.type`, Gemini `error.status|code` —
 * and NEVER the free-text `error.message`, which can embed a key fragment (the
 * OpenAI 401 body is literally "Incorrect API key provided: sk-proj-…"). Carried
 * on `LlmProviderHttpError` so the catch site can name *why* a 4xx happened
 * (`invalid_request_error · response_format`) without re-introducing the body —
 * the same secret-safety invariant that keeps the body out of Sentry/logs/storage.
 */
export interface LlmProviderErrorDetail {
  type?: string;
  code?: string;
  param?: string;
}

export class LlmProviderHttpError extends Error {
  readonly [LLM_PROVIDER_HTTP_BRAND] = true;

  constructor(
    /**
     * The adapter's human string, which by convention embeds the upstream response
     * body ("OpenAI API error: 401 - …"). REDACTED at construction — see below.
     */
    message: string,
    public readonly status: number,
    public readonly provider: LlmProvider,
    /**
     * Credential-safe structured detail (type/code/param). Body-free by
     * construction — see `LlmProviderErrorDetail` and `parseLlmProviderErrorDetail`.
     * Optional so existing throw sites without a parseable body stay valid.
     */
    public readonly providerError?: LlmProviderErrorDetail,
  ) {
    // This is the only provider-error family that carries the upstream BODY:
    // ChannelProviderHttpError refuses it outright ("the upstream body can carry a
    // credential fragment") and MediaProviderHttpError synthesizes its message, but the
    // LLM adapters embed `await response.text()` verbatim — and the OpenAI 401 body is
    // literally "Incorrect API key provided: sk-proj-…".
    //
    // The docs above say the catch site must log only status + provider, and the 4xx
    // path honours that. But `message` escapes anyway on the paths that stay loud: the
    // catch-all hands the Error to Sentry.captureException, whose sanitizer walks only
    // `args` — never `err.message`, which becomes the issue title verbatim — and
    // conversation-do persists `err.message` to the eval `error_message` column.
    //
    // Redacting here rather than at each throw site or at one sink covers every adapter
    // (pre-stream AND mid-stream) and every sink at once, and a new adapter cannot
    // forget it. Redact rather than drop the body: for a 5xx it is the only diagnostic
    // signal that survives, since `providerError` is empty exactly when we failed to
    // recognize the error. gh #3370.
    super(redactSecrets(message));
    this.name = 'LlmProviderHttpError';
  }
}

/**
 * A provider rejected end-user supplied content after accepting the request
 * transport (for example OpenAI `bio_policy` or an `image_*` code inside an
 * open stream). This is handled degradation: log at warn and fail safe, but do
 * not raise a tenant connection alert because the credential/config is healthy.
 */
const LLM_PROVIDER_CONTENT_BRAND = Symbol.for('wayai.LlmProviderContentError');

export class LlmProviderContentError extends Error {
  readonly [LLM_PROVIDER_CONTENT_BRAND] = true;

  constructor(
    message: string,
    public readonly provider: LlmProvider,
    public readonly providerError?: LlmProviderErrorDetail,
  ) {
    super(redactSecrets(message));
    this.name = 'LlmProviderContentError';
  }
}

export function isLlmProviderContentError(error: unknown): error is LlmProviderContentError {
  return (
    typeof error === 'object' &&
    error !== null &&
    LLM_PROVIDER_CONTENT_BRAND in error &&
    (error as Record<symbol, unknown>)[LLM_PROVIDER_CONTENT_BRAND] === true
  );
}

/** Cap each extracted field so a hostile/verbose provider value can't bloat a log
 * line, the persisted eval `error_message`, or the alert row. */
const PROVIDER_ERROR_FIELD_MAX = 200;

function safeErrorField(value: unknown, max: number): string | undefined {
  // String scalars only. A NUMERIC provider `code` is, across every supported
  // provider, just the HTTP status echoed back (Gemini `error.code`, OpenRouter's
  // HTTP-mirror code) — the alert/log already carries `status_code`, so lifting it
  // here only produces a redundant "· 400" next to "HTTP 400". Keeping strings
  // captures the useful enums (`invalid_api_key`, `INVALID_ARGUMENT`) and drops the
  // noise. gh #3164.
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

/**
 * The `{ error: {...} }` envelope both LLM providers and Meta Graph wrap their
 * failures in. Returns `undefined` for a non-JSON body, a body without a
 * structured `error` object, or any parse failure — never throws.
 */
function readErrorObject(body: string | undefined | null): Record<string, unknown> | undefined {
  if (!body || typeof body !== 'string') return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return undefined;
  }
  const err = (parsed as { error?: unknown } | null)?.error;
  return err && typeof err === 'object' ? (err as Record<string, unknown>) : undefined;
}

/**
 * Parse ONLY the credential-safe structured fields from an LLM provider's raw
 * error-response body. Returns `{}` for a non-JSON body, a body without a
 * structured `error` object, or any parse failure — never throws.
 *
 * Extracts an ALLOWLIST of scalar fields (`type`/`code`/`param`, plus Gemini's
 * `status` enum as the type-equivalent) and DELIBERATELY ignores `message` /
 * `metadata` — the free-text fields that can carry a key fragment. Shared by every
 * adapter so the "structured detail is safe, body is not" boundary lives in one
 * place. gh #3164.
 */
export function parseLlmProviderErrorDetail(body: string | undefined | null): LlmProviderErrorDetail {
  const e = readErrorObject(body);
  if (!e) return {};
  const detail: LlmProviderErrorDetail = {};
  // Gemini nests its type-equivalent enum under `status` (e.g. "INVALID_ARGUMENT").
  const type = safeErrorField(e.type, PROVIDER_ERROR_FIELD_MAX) ?? safeErrorField(e.status, PROVIDER_ERROR_FIELD_MAX);
  const code = safeErrorField(e.code, PROVIDER_ERROR_FIELD_MAX);
  const param = safeErrorField(e.param, PROVIDER_ERROR_FIELD_MAX);
  if (type) detail.type = type;
  if (code) detail.code = code;
  if (param) detail.param = param;
  return detail;
}

/**
 * Compact, credential-safe one-line description of a provider error's structured
 * detail (e.g. `invalid_request_error · response_format`), or `null` when no safe
 * detail is available. Built ONLY from the allowlisted type/code/param — never the
 * body/message. gh #3164.
 */
export function describeLlmProviderErrorDetail(detail: LlmProviderErrorDetail | undefined): string | null {
  if (!detail) return null;
  const parts = [detail.type, detail.code, detail.param].filter((p): p is string => !!p);
  return parts.length ? parts.join(' · ') : null;
}

// ---------------------------------------------------------------------------
// Meta Graph API error bodies
//
// Meta's top-level `error.message` is a CATEGORY, not a reason: `(#100) Invalid
// parameter` says only that some parameter was rejected. The actionable text
// arrives beside it — `error.error_data.details` (Meta's own troubleshooting
// guidance points there first), `error.error_user_msg`, `error.error_subcode`.
// A caller that keeps only `message` discards the reason at the moment it
// arrives, leaving the UI, the persisted state and Sentry with a string nobody
// can act on.
//
// `apps/backend/src/channels/meta-error.ts` reads the same envelope for the
// numeric code/subcode alone; these two compose the human-readable form. Other
// Meta callers still hand-roll their own summaries (`providers/refresh-token.ts`)
// — this is not yet the single composer for the repo.
// ---------------------------------------------------------------------------

/** Cap each lifted field, matching PROVIDER_ERROR_FIELD_MAX's purpose with the
 * larger budget Meta's `details` sentences need. */
const META_ERROR_FIELD_MAX = 300;

/** Cap the COMPOSED string. The per-field cap alone bounds each part but not
 * their sum: message + details + error_user_msg + subcode compose past 900
 * characters, and the result is written to `connection.state.registration_error`
 * in a DO row and to every log context that carries it. */
const META_ERROR_TOTAL_MAX = 500;

/** Separator between Meta's own message and the detail fields appended to it.
 * `isMetaPinRequiredMessage` splits on it to read the message alone. */
const META_ERROR_SEPARATOR = ' — ';

/**
 * The one shape redaction must NOT touch: a value that is entirely one unbroken
 * run of token characters. `redact.ts`'s `OPAQUE_SECRET_RE` is `^`-anchored, so
 * that is precisely — and only — the shape where handing it a field value
 * destroys a reason instead of a secret (`WHATSAPP_BUSINESS_ACCOUNT_NOT_VERIFIED`).
 * Everything else, a sentence or a JSON blob, carries a space or a `{`/`"`/`:`
 * that breaks the anchor, so redacting it is safe — and a blob is where an
 * echoed credential actually appears.
 */
const SINGLE_TOKEN_RUN = /^[A-Za-z0-9_-]+$/;

/**
 * Lift one Meta field: redact (unless it is a bare token run), then cap.
 * Capping strictly afterwards, because `JSON_CREDENTIAL_RE` needs the value's
 * closing quote — a cap landing inside a credential makes the pattern miss and
 * the fragment survives whole.
 */
function liftMetaField(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const safe = SINGLE_TOKEN_RUN.test(trimmed) ? trimmed : redactSecrets(trimmed);
  return safe.slice(0, META_ERROR_FIELD_MAX);
}

/**
 * `error_data` arrives as an object on most endpoints and as a JSON-encoded
 * STRING on some (Meta is not consistent across product surfaces). Both shapes
 * carry `details`, so both are read — a string that does not parse is ignored
 * rather than surfaced raw.
 *
 * The string shape is redacted here, and here only: escaping hides a credential's
 * quotes behind backslashes in the outer body (`\"access_token\":\"EAA…\"`), so
 * `JSON_CREDENTIAL_RE` cannot see it until this inner string is unescaped. This
 * value is a JSON blob, which is what makes redacting it safe — see
 * `describeMetaGraphError`.
 */
function readMetaErrorData(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(redactSecrets(value));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Compose a one-line, human-actionable description of a Meta Graph API error
 * response body. Never throws: a non-JSON body (an HTML error page from an edge
 * proxy) or a body with no `error` object falls back to the raw text, then to
 * `fallback`.
 *
 * The top-level `message` is always the PREFIX, never replaced — callers read it
 * back out by splitting on `META_ERROR_SEPARATOR`.
 *
 * `redactSecrets` is applied for the reason `channels/meta-error.ts` already
 * states: an OAuthException `message` can embed a token fragment. Callers write
 * this string to sinks the logger's own sanitizer never sees — a DO row and an
 * HTTP response body — so the redaction belongs here, where no caller can forget
 * it. Mirrors `LlmProviderHttpError`, which redacts the embedded body at
 * construction for the same reason.
 *
 * ONE rule governs where it runs, and a field added later must follow it (lift
 * it with `liftMetaField`): **redact before capping, and skip only a value that
 * is entirely one token run.** Both halves are load-bearing; both alternatives
 * were tried and each fails in its own direction:
 *
 *  - Redacting AFTER a cap leaks. `JSON_CREDENTIAL_RE` needs the value's closing
 *    quote, so a cap landing inside a credential makes the pattern miss and the
 *    fragment survives whole.
 *  - Redacting EVERY value unconditionally over-redacts. `OPAQUE_SECRET_RE` is
 *    `^`-anchored, so a value that is one unbroken run is replaced entirely —
 *    turning a reason like `WHATSAPP_BUSINESS_ACCOUNT_NOT_VERIFIED` into
 *    `[REDACTED]` and destroying exactly what this helper exists to surface.
 *
 * KNOWN RESIDUAL: a credential that arrives as a bare token under a key
 * `redact.ts` does not recognize is the one shape this leaves intact — it is
 * indistinguishable, by shape alone, from a reason enum. See the PR's
 * `## Unresolved` section.
 */
export function describeMetaGraphError(body: string | undefined | null, fallback: string): string {
  // Redacted once, as a blob, before anything is parsed or capped.
  const raw = typeof body === 'string' ? redactSecrets(body.trim()) : '';
  const e = readErrorObject(raw);
  if (!e) return raw.slice(0, META_ERROR_TOTAL_MAX) || fallback;

  const base = liftMetaField(e.message) || fallback;
  const errorData = readMetaErrorData(e.error_data);

  const parts: string[] = [];
  const details = liftMetaField(errorData?.details);
  const userMsg = liftMetaField(e.error_user_msg);
  if (details) parts.push(details);
  // Only when it adds something: Meta often repeats `details` verbatim here.
  if (userMsg && userMsg !== details) parts.push(userMsg);
  if (typeof e.error_subcode === 'number') parts.push(`subcode ${e.error_subcode}`);

  const composed = parts.length ? `${base}${META_ERROR_SEPARATOR}${parts.join(META_ERROR_SEPARATOR)}` : base;
  // No second redaction pass here. It would be a backstop only for a field
  // appended UNCAPPED, and it reintroduces the over-redaction above whenever
  // `composed` is a single part with no separator to break the anchored match.
  return composed.slice(0, META_ERROR_TOTAL_MAX);
}

/**
 * True when Meta rejected a WhatsApp `/register` because the number has two-step
 * verification enabled and no PIN was sent (`(#100) The parameter pin is
 * required.`). The registration dialog reveals its PIN field on it.
 *
 * Reads ONLY Meta's own message — the segment before the first
 * `META_ERROR_SEPARATOR`. The appended detail fields are Meta free text in the
 * tenant's locale, and a sentence there that merely mentions a PIN is not Meta
 * demanding one. Meta ships no stable code for this rejection, so the substring
 * is the only signal; narrowing the haystack is what keeps it honest.
 */
export function isMetaPinRequiredMessage(message?: string | null): boolean {
  if (!message) return false;
  return message.split(META_ERROR_SEPARATOR)[0].toLowerCase().includes('pin is required');
}

export function isLlmProviderHttpError(error: unknown): error is LlmProviderHttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    LLM_PROVIDER_HTTP_BRAND in error &&
    (error as Record<symbol, unknown>)[LLM_PROVIDER_HTTP_BRAND] === true
  );
}

/**
 * Internal signal: an upstream LLM request exceeded the adapter's client-side
 * timeout and was aborted (`AbortController`). This is a transient slowness /
 * network blip — the same retryable shape as a 503/529 (the provider didn't
 * reject the request, it just didn't answer in time), NOT a code defect or a
 * wrong request. Folded into `isRetryableLlmError` (gh #2873) so the turn retries
 * it in a later attempt (warn, not error — never inside its model step: the try
 * already took the adapter's whole timeout, past the step's in-turn cut-off),
 * the LLM circuit breaker excludes it from its failure count,
 * and it only reaches Sentry on retry-exhaustion — exactly the 503/529 path. Before this, every
 * adapter threw a bare `Error('… timed out.')` with no code/status, so it fell
 * through to the `logger.error` catch-all → Sentry/autofix + a failed turn +
 * breaker pressure (Sentry WAYAI-BACKEND-70).
 *
 * Distinct from `LlmProviderHttpError`: a timeout is a CLIENT-side abort with NO
 * upstream HTTP status, so it must NOT feed `recordProviderDegradation` (which
 * keys on a provider 5xx to raise a `provider:<x>` Status & Notices broadcast) —
 * a client abort is not the provider returning 5xx. Carries `provider` for log
 * attribution; `message` keeps the adapter's existing human string so
 * callers/tests are unchanged.
 */
const LLM_PROVIDER_TIMEOUT_BRAND = Symbol.for('wayai.LlmProviderTimeoutError');

export class LlmProviderTimeoutError extends Error {
  readonly [LLM_PROVIDER_TIMEOUT_BRAND] = true;

  constructor(
    message: string,
    public readonly provider: LlmProvider,
  ) {
    super(message);
    this.name = 'LlmProviderTimeoutError';
  }
}

export function isLlmProviderTimeoutError(error: unknown): error is LlmProviderTimeoutError {
  return (
    typeof error === 'object' &&
    error !== null &&
    LLM_PROVIDER_TIMEOUT_BRAND in error &&
    (error as Record<symbol, unknown>)[LLM_PROVIDER_TIMEOUT_BRAND] === true
  );
}

const LLM_PROVIDER_DISPLAY_NAME: Record<LlmProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  openrouter: 'OpenRouter',
  gemini: 'Gemini',
  xai: 'xAI',
  typesafe: 'TypeSafe',
};

/**
 * Credential-safe, client-facing description of a failed LLM turn (gh #2670).
 *
 * Surfaced to the operator/CLI in place of an opaque `'Processing failed'` so a
 * provider rejection (e.g. Gemini 400/500 on an unsupported tool schema) is
 * self-diagnosable. Derived ONLY from the structured `status` + `provider` —
 * NEVER from `error.message`, which embeds the raw upstream body that can carry a
 * credential fragment (the same invariant that keeps the body out of Sentry).
 * Non-provider errors fall back to the generic string (no safe detail to add).
 */
export function describeLlmHttpErrorForClient(error: unknown): string {
  if (isLlmProviderHttpError(error)) {
    const provider = LLM_PROVIDER_DISPLAY_NAME[error.provider] ?? error.provider;
    const base = error.status >= 400 && error.status < 500
      ? `${provider} rejected the request (${error.status})`
      : `${provider} upstream error (${error.status})`;
    // Append the credential-safe structured reason when the provider gave one
    // (e.g. "… (400): invalid_request_error · response_format") so a config
    // rejection is self-diagnosable instead of an opaque status. gh #3164.
    const detail = describeLlmProviderErrorDetail(error.providerError);
    return detail ? `${base}: ${detail}` : base;
  }
  return 'Processing failed';
}

/**
 * True when `error` is an upstream LLM provider 4xx — a client/auth error
 * (almost always a customer credential misconfiguration). These belong at
 * logger.warn (Loki), NOT logger.error (Sentry). gh #2539.
 *
 * Note 429 never reaches here: every adapter throws LlmRateLimitError for 429
 * before the generic non-OK path, so the [400,500) range is exactly the
 * client/auth class. A retryable 5xx (503/529) is handled by isRetryableLlmError,
 * not here; any other 5xx stays at error (genuine outage / our-side fault).
 */
export function isExternalLlmHttpError(error: unknown): error is LlmProviderHttpError {
  return isLlmProviderHttpError(error) && error.status >= 400 && error.status < 500;
}

/**
 * Map a tenant-actionable provider HTTP status to a Status & Notices alert
 * category — the axis the hub admin acts on:
 *   - 402/429 (payment / quota) → `connection_quota` (upgrade or pay);
 *   - 401/403 (authentication / authorization) → `connection_auth` (rotate /
 *     re-enter the credential);
 *   - everything else in the 4xx class (400 `invalid_request_error`, 404, 422 …)
 *     → `connection_config` (a request/config problem, NOT a bad credential).
 *
 * The 400-vs-401 split matters: a 400 is the provider rejecting the request shape
 * (e.g. an unsupported `response_format`), so labeling it "credential invalid"
 * points the admin at rotating a healthy key. gh #3164. The one exception is a
 * provider that rejects the credential itself under a 400 (Google's `API_KEY_INVALID`):
 * status alone cannot say so, so the throw site reads the body and sets
 * `MediaProviderHttpError.credentialRejected`, and `recordMediaConnectionAlert` files
 * that as `connection_auth` without asking this function. Note: for LLM providers
 * 429 throws LlmRateLimitError before the 4xx boundary, so the LLM path only ever
 * yields `connection_auth` / `connection_config`; channel/STT/TTS producers (which
 * do surface 402/429) exercise the quota branch.
 */
export function statusToAlertCategory(
  status: number,
): 'connection_auth' | 'connection_quota' | 'connection_config' {
  if (status === 402 || status === 429) return 'connection_quota';
  if (status === 401 || status === 403) return 'connection_auth';
  return 'connection_config';
}

/**
 * Upstream LLM HTTP statuses that are transient and explicitly retryable — the
 * provider told us to back off and try again, not that the request was wrong:
 *
 * - 503 — Service Unavailable (Gemini `UNAVAILABLE` "high demand", OpenAI
 *   overloaded): the canonical "try again later" status.
 * - 529 — Anthropic `overloaded_error`: Anthropic's transient-overload status.
 *
 * 500/502/504 are deliberately OUT: an unexpected 5xx is a genuine fault worth
 * investigating, so it stays at logger.error → Sentry (gh #2539). A *sustained*
 * 503/529 still reaches Sentry via the retry-exhaustion path — only a transient
 * blip is downgraded to warn + reschedule.
 */
const RETRYABLE_LLM_PROVIDER_STATUSES: ReadonlySet<number> = new Set([503, 529]);

/**
 * True when `error` is a transient, retryable upstream LLM error that the turn
 * retries rather than fails — its model step in the turn when the wait is short,
 * a later attempt of the turn otherwise (wayai#5083 PR 8b-3a) — a 429
 * (LlmRateLimitError), a provider 503/529 (LlmProviderHttpError), or a
 * client-side request timeout (LlmProviderTimeoutError). The model step's retry
 * and the ConversationDO catch site's retry branch key on this, and the LLM circuit breaker
 * excludes it from the failure count (a throttle/overload/timeout blip is not
 * provider unhealth). Classified here so it covers every adapter — they all
 * already throw LlmProviderHttpError for a non-429 non-OK response and
 * LlmProviderTimeoutError on an abort. gh #2659, gh #2873.
 */
export function isRetryableLlmError(error: unknown): boolean {
  return (
    isLlmRateLimitError(error) ||
    isLlmProviderTimeoutError(error) ||
    (isLlmProviderHttpError(error) && RETRYABLE_LLM_PROVIDER_STATUSES.has(error.status))
  );
}

/**
 * 502 - Bad Gateway: External service error
 */
export class ExternalServiceError extends AppError {
  constructor(service: string, originalError?: Error) {
    super(`External service error: ${service}`, 'EXTERNAL_SERVICE_ERROR', 502, {
      service,
      originalMessage: originalError?.message,
    });
    this.name = 'ExternalServiceError';
  }
}

/**
 * An external channel-send API rejected an outbound message with an HTTP status —
 * Meta WhatsApp / Instagram Graph or the Telegram Bot API (Telegram surfaces its
 * status as `error_code` on an HTTP-200 `ok:false` body, which we carry as `status`).
 *
 * Extends `ExternalServiceError` so the existing `instanceof ExternalServiceError`
 * delivery catches keep treating it as an expected degraded condition (warn → Loki,
 * NOT Sentry); adds the numeric upstream `status` the Status & Notices producer keys
 * on to raise a tenant-actionable `channel_token` / `connection_quota` alert for the
 * 4xx class. Mirrors `LlmProviderHttpError` / `McpError`.
 *
 * NEVER carries the upstream response body. A Meta error body embeds an
 * OAuthException whose message can include a token fragment, so — unlike the plain
 * `ExternalServiceError('WhatsApp', new Error('API error 401: …body…'))` it replaces —
 * it passes NO `originalError`, keeping only `provider` + `status` and the **numeric**
 * provider error `code`/`subcode` (Meta's `error.code` / `error.error_subcode`). Those
 * are small integers, not secrets — they let consumers classify the failure (token
 * vs. per-recipient vs. outside-window) without re-introducing the body:
 * `providerCode === 190` = Meta OAuthException (expired/invalid token);
 * `providerCode === 131047` = WhatsApp outside-24h-window; `providerCode === 10` +
 * `providerSubcode === 2534022` = Instagram outside-window. (Named `providerCode`,
 * not `code`, because `AppError.code` is already the string error-code enum.)
 */
const CHANNEL_PROVIDER_HTTP_BRAND = Symbol.for('wayai.ChannelProviderHttpError');

export class ChannelProviderHttpError extends ExternalServiceError {
  readonly [CHANNEL_PROVIDER_HTTP_BRAND] = true;
  readonly status: number;
  readonly provider: string;
  /** Provider numeric error code (Meta `error.code`) — body-free. */
  readonly providerCode?: number;
  /** Provider numeric error subcode (Meta `error.error_subcode`) — body-free. */
  readonly providerSubcode?: number;

  constructor(provider: string, status: number, providerCode?: number, providerSubcode?: number) {
    // No originalError on purpose: ExternalServiceError stores originalError.message
    // in details.originalMessage, and the upstream body can carry a credential
    // fragment. Only the numeric status + code/subcode survive.
    super(provider);
    this.name = 'ChannelProviderHttpError';
    this.status = status;
    this.provider = provider;
    this.providerCode = providerCode;
    this.providerSubcode = providerSubcode;
  }
}

export function isChannelProviderHttpError(error: unknown): error is ChannelProviderHttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    CHANNEL_PROVIDER_HTTP_BRAND in error &&
    (error as Record<symbol, unknown>)[CHANNEL_PROVIDER_HTTP_BRAND] === true
  );
}

/**
 * True when `error` is an external channel-send 4xx — a tenant-actionable
 * credential/config/quota condition (expired/revoked channel token, rate/quota).
 * The ConversationDO delivery catch raises a Status & Notices alert for these; a
 * 5xx (provider outage) stays observability-only. Mirrors `isExternalLlmHttpError`.
 */
export function isExternalChannelHttpError(error: unknown): error is ChannelProviderHttpError {
  return isChannelProviderHttpError(error) && error.status >= 400 && error.status < 500;
}

/**
 * An external media-provider (STT/TTS) API rejected a request with an HTTP status —
 * speech-to-text (Groq, OpenAI) or text-to-speech (ElevenLabs, OpenAI-compatible, Gemini).
 * These run on hub credentials and are billable, so a bad/expired key or quota is a
 * tenant-actionable condition the hub admin fixes — a 4xx belongs at `logger.warn`
 * (Loki), NOT `logger.error` (Sentry → autofix, the anti-pattern this removes), and
 * raises a Status & Notices alert. Mirrors `LlmProviderHttpError` / `ChannelProviderHttpError`.
 *
 * NEVER carries the upstream response body (a provider error message can name the
 * key) — only the numeric `status` + `provider` display name. `message` is a body-free
 * `"Groq API error (401)"`; the catch site logs only `status`/`provider`.
 */
const MEDIA_PROVIDER_HTTP_BRAND = Symbol.for('wayai.MediaProviderHttpError');

export class MediaProviderHttpError extends Error {
  readonly [MEDIA_PROVIDER_HTTP_BRAND] = true;
  /**
   * The provider said the credential itself was rejected, under a status that does not say so
   * (Google answers an invalid or expired key with 400 `API_KEY_INVALID`, not 401). Read off
   * the body at the throw site, which carries only this flag on. Alerting keys on it so the
   * admin is told to re-enter the key, not that the key is fine.
   */
  readonly credentialRejected: boolean;

  constructor(
    public readonly provider: string,
    public readonly status: number,
    options: { credentialRejected?: boolean } = {},
  ) {
    super(`${provider} API error (${status})`);
    this.name = 'MediaProviderHttpError';
    this.credentialRejected = options.credentialRejected ?? false;
  }
}

export function isMediaProviderHttpError(error: unknown): error is MediaProviderHttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    MEDIA_PROVIDER_HTTP_BRAND in error &&
    (error as Record<symbol, unknown>)[MEDIA_PROVIDER_HTTP_BRAND] === true
  );
}

/**
 * True when `error` is an external media-provider (STT/TTS) 4xx — a tenant-actionable
 * credential/quota condition. The ConversationDO transcribe/synthesize catch raises a
 * Status & Notices alert for these; a 5xx (provider outage) stays observability-only.
 */
export function isExternalMediaHttpError(error: unknown): error is MediaProviderHttpError {
  return isMediaProviderHttpError(error) && error.status >= 400 && error.status < 500;
}

/**
 * One source entity a hub-branching clone/sync left incomplete in the
 * destination hub, of one of two kinds. `entity_id` is the SOURCE id in both;
 * `error` is the failure's message, truncated at the collection site.
 *
 * - **A write failure** (every clone, sync and replicate step): the entity was
 *   never written. No destination id was assigned, no ID-mapping entry was
 *   written for it, and a later sync re-creates it.
 * - **A `subscribe` failure** (`entity_type: 'automation'`, a publish or sync
 *   into production only, wayai#4931 D12) — the one statement of this rule; the
 *   other records point here. The automation IS in production, written by this
 *   publish, and in its ID mapping (`entity_id` below is null where it is not).
 *   What did not apply is this publish's change to its source hub's
 *   subscription set for this hub: the publish sends that hub the whole set
 *   (`reconcileEventSubscriptions`), so every enabled production automation
 *   naming it as `source_hub` gets an entry, whether this publish changed it or
 *   not.
 *   - The source refused (revoked, full, not allowed): `setEventSubscriptions`
 *     applied none of the set, and the source keeps the one it held before (none
 *     after a revocation, which deleted it). A subscription already in that set
 *     keeps delivering, so an automation whose subscription the publish left
 *     unchanged keeps hearing its events (`receiveCrossHubEvent`).
 *   - The source could not be reached: the call failed, and the change may or
 *     may not have applied.
 *   `entity_id` is the preview automation's id, mapped back from the production
 *   row (null when the mapping holds none), and `error` names the automation and
 *   the source hub, which is how it is told from a write failure. The next
 *   publish or sync applies the change once the source accepts it; a source that
 *   still refuses it refuses that publish before it writes instead.
 */
export interface HubBranchingEntityFailure {
  entity_type: string;
  entity_id: string | null;
  error: string;
}

/**
 * 500 — a publish/sync/replicate finished but one or more entities failed to
 * clone into the destination hub, so it landed incomplete (gh #3468), or a
 * publish or sync left `subscribe` entries (see `HubBranchingEntityFailure`). The
 * branching path is best-effort-incremental by design: the destination hub is
 * still fully created, linked, and its ID mapping persisted, so a later sync
 * re-creates the skipped entities once the underlying cause is fixed. But the
 * operation MUST NOT report unqualified success — it did not complete all the
 * caller asked to publish. The message counts every entry as an entity write.
 *
 * Deliberately NOT in SENTRY_SKIP_CODES: an incomplete branch is genuinely
 * unexpected, so it is captured (with the failed-entity list in `details`) for
 * the autofix pipeline.
 */
export class HubBranchingIncompleteError extends AppError {
  constructor(
    scope: 'publish' | 'sync' | 'replicate',
    hubId: string,
    failures: HubBranchingEntityFailure[],
  ) {
    super(
      `Hub ${scope} landed incomplete: ${failures.length} entity write(s) failed`,
      'HUB_BRANCHING_INCOMPLETE',
      500,
      { scope, hub_id: hubId, failed_count: failures.length, failed: failures },
    );
    this.name = 'HubBranchingIncompleteError';
  }
}

/**
 * 503 - Service Unavailable: Circuit breaker open
 */
export class CircuitBreakerOpenError extends AppError {
  constructor(service: string) {
    super(`Service temporarily unavailable: ${service}`, 'CIRCUIT_BREAKER_OPEN', 503, {
      service,
    });
    this.name = 'CircuitBreakerOpenError';
  }
}

/**
 * 503 - Service Unavailable: Generic service unavailability
 */
export class ServiceUnavailableError extends AppError {
  constructor(message = 'Service temporarily unavailable', details?: Record<string, unknown>) {
    super(message, 'SERVICE_UNAVAILABLE', 503, details);
    this.name = 'ServiceUnavailableError';
  }
}

/** 503 — CF DO storage transient (Sentry-skipped via TRANSIENT_STORAGE). */
export class TransientStorageError extends AppError {
  constructor(message = 'Storage temporarily unavailable') {
    super(message, 'TRANSIENT_STORAGE', 503);
    this.name = 'TransientStorageError';
  }
}

/**
 * 503 — ClickHouse cold-start (Cloud auto-suspend resume). Retryable,
 * Sentry-skipped via CLICKHOUSE_COLD.
 *
 * Thrown by detail-style CH routes (archive transcript / observability) whose
 * response contract can't carry a `cold: true` flag — an opaque blob would
 * silently render as an empty transcript. They surface this retryable error
 * instead; the matching `withClickHouseQuery` wake probe means the client's
 * retry succeeds once CH is warm. List/dashboard CH routes still return
 * `{ cold: true }` 200, since their consumers handle the cold banner.
 */
export class ClickHouseColdStartError extends AppError {
  constructor(message = 'Conversation store is warming up, please retry') {
    super(message, 'CLICKHOUSE_COLD', 503, { retryable: true });
    this.name = 'ClickHouseColdStartError';
  }
}

/**
 * AppError codes that represent expected client-correctable conditions —
 * the backend's onError handler skips Sentry for these, and the frontend
 * logger mirrors the same policy (console logs still hit Loki).
 *
 * Single source of truth used by both workers/backend/src/middleware/error-handler.ts
 * and frontend/src/lib/logger.ts.
 */
export const SENTRY_SKIP_CODES: ReadonlySet<string> = new Set([
  'VALIDATION_ERROR',
  'AUTHENTICATION_ERROR',
  'AUTHORIZATION_ERROR',
  'NOT_FOUND',
  'RATE_LIMIT_EXCEEDED',
  // An end user at their hub's daily message limit: the hub's own setting, working as set.
  END_USER_DAILY_LIMIT_CODE,
  // A customer page's viewer whose account names no one customer for the hub: their contact book's state.
  PAGE_CUSTOMER_UNAVAILABLE_CODE,
  // 409 — expected client-correctable state conflicts (already-exists,
  // locked, "in use by …"). A genuine bug surfaces as a 5xx, never a 409.
  'CONFLICT',
  // CF DO storage transients (object reset / op timeout) — gh #1966.
  'TRANSIENT_STORAGE',
  // ClickHouse Cloud cold-start (auto-suspend resume) — expected/transient,
  // recovered by the withClickHouseQuery wake probe + client retry.
  'CLICKHOUSE_COLD',
]);

/**
 * Check if an error is an AppError.
 * Uses Symbol brand instead of instanceof to work across module boundaries.
 */
export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === 'object' &&
    error !== null &&
    APP_ERROR_BRAND in error &&
    (error as Record<symbol, unknown>)[APP_ERROR_BRAND] === true
  );
}

/**
 * True when `error` is a Cloudflare DO SQLite UNIQUE-constraint violation
 * (`SQLITE_CONSTRAINT_UNIQUE`), across the RPC boundary or in-DO.
 *
 * A route that guards a uniqueness invariant with a read-then-check pre-check
 * (`list…()` → `.some(...)` → ConflictError) is racy: a concurrent double-submit
 * lets both requests pass the check, then both INSERT, and the second trips the
 * UNIQUE index. The DO raises a bare `Error` whose message is preserved across
 * the RPC boundary, so — not being an AppError — it escapes as a 500 instead of
 * the intended 409. Catch sites use this to convert the loser of the race into
 * the same ConflictError the pre-check would have thrown, making the INSERT (not
 * the pre-check) the source of truth for the invariant. The pre-check stays as a
 * fast path. gh #3465.
 *
 * Matches on the canonical SQLite message substring — the extended code
 * `SQLITE_CONSTRAINT_UNIQUE` is not exposed as a structured field on the thrown
 * Error, and the message survives RPC serialization intact (the stack does not).
 */
export function isUniqueConstraintError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('UNIQUE constraint failed');
}

/**
 * Derive a stable, PII-free grouping token from an error message.
 *
 * Sentry's default grouping for the backend Worker is unreliable: distinct
 * errors collapse to the same stack frames and group under one issue
 * (gh #2440). Every backend Sentry capture path therefore sets an explicit
 * fingerprint, and that fingerprint needs a message discriminator — but a raw
 * message can embed user input (library errors interpolate values, e.g.
 * `Invalid phone number: ${e.message}`). This normalizes a message into a token
 * that is stable across interpolated values yet distinct across different
 * logical errors, without leaking user data.
 *
 * Replacement order matters: URLs/emails first (they contain digits and dots),
 * then ids, then quoted strings, then bare numbers.
 */
export function errorMessageShape(message: string): string {
  // Bound the input before the regex chain: this runs in the per-event
  // beforeSend hook, so a pathologically long message must not turn it into a
  // perf/DoS sink. 256 keeps enough to discriminate; the trailing slice caps
  // the emitted token at 120.
  return (message ?? '')
    .slice(0, 256)
    .replace(/https?:\/\/\S+/gi, '<url>')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/gi, '<email>')
    .replace(
      // No \b anchors: a UUID may abut a word char (e.g. `<uuid>internal`); the
      // 8-4-4-4-12 dash shape is specific enough that mid-string matching is safe.
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '<uuid>',
    )
    .replace(/\b[0-9a-f]{16,}\b/gi, '<hex>')
    // Mask quoted values per quote type. A single combined class ["'`][^"'`]*["'`]
    // mis-pairs across types (e.g. `"it's valid"` matches only `"it'`), leaking
    // the tail — so strip each quote type independently. Single quotes go last,
    // so an apostrophe inside a double- or backtick-quoted value is gone before
    // the single-quote rules read the message.
    .replace(/"[^"]*"/g, '<str>')
    .replace(/`[^`]*`/g, '<str>')
    // A single quote mark is also an apostrophe (can't, O'Brien, Dennis'), so
    // single quotes are not paired: a mark opens only where no letter or digit
    // precedes it, closes only where none follows it, and everything from the
    // first opener to the last closer goes. A value quoted at word edges goes
    // whole, whatever apostrophes it holds. So does the text between two quoted
    // values, or between a value and a later word-final apostrophe (`'X' is not
    // in the members' list` → `<str> list`). A value whose closing quote falls
    // past the 256 cut stays. The opener captures the character before it
    // rather than using a lookbehind: the web client imports this module, and a
    // lookbehind literal is a syntax error in Safari before 16.4.
    .replace(/(^|[^\p{L}\p{M}\p{N}])'[\s\S]*'(?![\p{L}\p{M}\p{N}])/gu, '$1<str>')
    // Of the marks left, two neighbours pair when the first has no letter or
    // digit before it or the second none after it. So a value whose quote is
    // glued to a letter on one side (`'${name}'s`, `x'${value}'`) is masked
    // unless it holds an apostrophe itself, while two contractions (`can't …
    // it's`) stay text, and so does a value glued to letters on both sides.
    .replace(/(^|[^\p{L}\p{M}\p{N}])'[^']*'|'[^']*'(?![\p{L}\p{M}\p{N}])/gu, '$1<str>')
    // No trailing \b: a number abutting a unit letter (`30.5s`, `1.5GB`) would
    // otherwise match only `30.` and leave a stray digit, which both leaks and
    // splits grouping across values. The leading \b still prevents matching
    // digits inside an opaque token before the <id> rule below can claim it.
    .replace(/\b\d[\d.,]*/g, '<n>')
    // Long opaque tokens that survive the rules above (WorkOS user_/org_ ids,
    // way_/wst_ API tokens, slugs): mask so the fingerprint can't fragment by
    // identifier or carry token-shaped data — mirrors the >20-char heuristic in
    // logger.ts's sanitizeForSentry.
    .replace(/\b[A-Za-z0-9][A-Za-z0-9_-]{19,}\b/g, '<id>')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .slice(0, 120);
}
